"use client";

import * as React from "react";

import { Input as HeadlessInput } from "@headlessui/react";

import Typography from "@/components/flowbite/typography/typography";
import { cn } from "@/shared/helpers/cn.helper";

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "required" | "disabled"
> {
  label?: string;
  error?: boolean;
  warning?: boolean;
  errorMessage?: string;
  required?: boolean;
  hint?: string;
  icon?: React.ReactNode;
  iconBefore?: React.ReactNode;
  type?: React.HTMLInputTypeAttribute;
  disabled?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  iconContainerClassName?: string;
}

interface InputVisualState {
  isDefaultState: boolean;
  isErrorState: boolean;
  isFocusedState: boolean;
  isInactiveState: boolean;
  isWarningState: boolean;
}

function buildAriaDescribedBy(options: {
  error: boolean;
  errorId?: string;
  errorMessage?: string;
  hint?: string;
  hintId?: string;
}): string | undefined {
  const describedBy: string[] = [];

  if (options.error && options.errorMessage && options.errorId) {
    describedBy.push(options.errorId);
  }

  if (options.hint && options.hintId) {
    describedBy.push(options.hintId);
  }

  return describedBy.length > 0 ? describedBy.join(" ") : undefined;
}

function getInputVisualState(options: {
  disabled: boolean;
  error: boolean;
  isFocused: boolean;
  warning: boolean;
}): InputVisualState {
  const isInactiveState = options.disabled;
  const isErrorState = options.error && !isInactiveState;
  const isWarningState = options.warning && !options.error && !isInactiveState;
  const isDefaultState = !options.error && !options.warning && !options.isFocused && !isInactiveState;
  const isFocusedState = !options.error && !options.warning && options.isFocused && !isInactiveState;

  return {
    isDefaultState,
    isErrorState,
    isFocusedState,
    isInactiveState,
    isWarningState,
  };
}

function getInputTextColorClass(visualState: InputVisualState, hasValue: boolean): string {
  const { isErrorState, isFocusedState, isInactiveState } = visualState;

  return cn(
    isInactiveState && "text-text-tertiary",
    isErrorState && "text-text-primary",
    isFocusedState && !hasValue && "text-text-tertiary",
    hasValue && !isInactiveState && "text-text-primary",
    !hasValue && !isFocusedState && !isInactiveState && "text-text-tertiary",
  );
}

function InputLabel({
  inputId,
  label,
  labelClassName,
  required,
}: {
  inputId?: string;
  label: string;
  labelClassName?: string;
  required: boolean;
}) {
  return (
    <label
      className={cn(
        "block",
        Typography.classes.Body.default,
        Typography.classes.Body.classnames.sRegular,
        "text-text-secondary",
        labelClassName,
      )}
      htmlFor={inputId}
    >
      {label}
      {required ? (
        <span
          aria-label="required"
          className="ml-0.5 text-btn-danger"
        >
          *
        </span>
      ) : null}
    </label>
  );
}

function InputIcons({
  icon,
  iconBefore,
  iconContainerClassName,
}: Pick<InputProps, "icon" | "iconBefore" | "iconContainerClassName">) {
  return (
    <>
      {icon ? (
        <div
          className={cn(
            "absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1",
            iconContainerClassName,
          )}
        >
          {icon}
        </div>
      ) : null}

      {iconBefore ? (
        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          {iconBefore}
        </div>
      ) : null}
    </>
  );
}

function InputFooter({
  error,
  errorId,
  errorMessage,
  hint,
  hintId,
}: {
  error: boolean;
  errorId?: string;
  errorMessage?: string;
  hint?: string;
  hintId?: string;
}) {
  if (error && errorMessage) {
    return (
      <p
        className="mt-1 text-xs text-btn-danger"
        id={errorId}
        role="alert"
      >
        {errorMessage}
      </p>
    );
  }

  if (hint) {
    return (
      <p
        className="mt-1 text-xs text-text-secondary"
        id={hintId}
      >
        {hint}
      </p>
    );
  }

  return null;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      labelClassName,
      inputClassName,
      iconContainerClassName,
      type = "text",
      label,
      error = false,
      warning = false,
      errorMessage,
      required = false,
      disabled = false,
      hint,
      icon,
      iconBefore,
      name,
      id,
      value,
      defaultValue,
      onFocus,
      onBlur,
      onChange,
      "aria-describedby": ariaDescribedByProp,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(
      defaultValue?.toString() ?? "",
    );

    // Handle controlled/uncontrolled pattern
    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    // Generate IDs for accessibility
    const inputId = id ?? name;
    const errorId = name ? `${name}-error` : undefined;
    const hintId = name ? `${name}-hint` : undefined;

    const visualState = getInputVisualState({ disabled, error, warning, isFocused });
    const hasValue = Boolean(currentValue);
    const textColorClass = getInputTextColorClass(visualState, hasValue);

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        if (!isControlled) {
          setInternalValue(newValue);
        }

        onChange?.(e);
      },
      [isControlled, onChange],
    );

    return (
      <div className={cn("flex flex-col gap-1", className)}>
        {label ? (
          <InputLabel
            inputId={inputId}
            label={label}
            labelClassName={labelClassName}
            required={required}
          />
        ) : null}

        <div className="relative">
          <HeadlessInput
            {...props}
            ref={ref}
            aria-describedby={
              ariaDescribedByProp
              ?? buildAriaDescribedBy({ error, errorMessage, errorId, hint, hintId })
            }
            aria-invalid={error}
            aria-required={required}
            className={cn(
              "pl-3 pr-2.5",
              iconBefore && "pl-10",
              icon && "pr-10",
              "h-10.5 max-h-10.5 min-h-10.5 w-full",
              "flex items-center justify-center",
              "bg-white",
              "rounded-xl border border-border-2",
              "focus:outline-none",
              Typography.classes.Body.default,
              Typography.classes.Body.classnames.mMedium,
              "fill-btn-icon stroke-btn-icon",
              "placeholder:text-text-tertiary",
              visualState.isDefaultState && "",
              visualState.isFocusedState && "border-border-3",
              visualState.isWarningState && "border-yellow-500",
              visualState.isErrorState && "border-btn-danger",
              visualState.isInactiveState && "cursor-not-allowed bg-background-primary",
              textColorClass,
              inputClassName,
            )}
            disabled={disabled}
            id={inputId}
            name={name}
            type={type}
            value={currentValue}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
          />

          <InputIcons
            icon={icon}
            iconBefore={iconBefore}
            iconContainerClassName={iconContainerClassName}
          />
        </div>

        <InputFooter
          error={error}
          errorId={errorId}
          errorMessage={errorMessage}
          hint={hint}
          hintId={hintId}
        />
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
